import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, ArrowRight, Calendar as CalendarIcon, Check, Loader2, Share2, Download, QrCode as QrIcon, Home, PartyPopper, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatCurrency, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { QRCodeCanvas } from "qrcode.react";

export default function BookingFlow() {
  const { kind } = useParams(); // "reservation" | "realtime"
  const [searchParams] = useSearchParams();
  const context = searchParams.get("context") || "menu"; // "menu" (no cash) | "cash" (all)
  const nav = useNavigate();
  const { company } = useAuth();
  const isRealtime = kind === "realtime";
  const currency = company?.currency || "FCFA";

  const [step, setStep] = useState(1);
  const [cities, setCities] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [pms, setPms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Step 1
  const today = new Date();
  const [fromCityId, setFromCityId] = useState("");
  const [toRegion, setToRegion] = useState("");
  const [toCityId, setToCityId] = useState("");
  const [date, setDate] = useState(today);

  // Step 2
  const [schedule, setSchedule] = useState("");
  const [pmId, setPmId] = useState("");
  const [customerFirst, setCustomerFirst] = useState("");
  const [customerLast, setCustomerLast] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/cities"),
      api.get("/routes"),
      api.get("/payment-methods"),
    ]).then(([c, r, p]) => {
      setCities(c.data);
      setRoutes(r.data);
      const isCash = (name) => /esp[èe]ces?|cash/i.test(name || "");
      const active = p.data.filter((x) => x.active);
      setPms(context === "cash" ? active : active.filter((x) => !isCash(x.name)));
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [context]);

  const activeRoute = useMemo(() => {
    return routes.find((r) => r.from_city_id === fromCityId && r.to_city_id === toCityId);
  }, [routes, fromCityId, toCityId]);

  const availableRegions = useMemo(() => {
    const rs = routes
      .filter((r) => !fromCityId || r.from_city_id === fromCityId)
      .map((r) => r.to_region)
      .filter(Boolean);
    return [...new Set(rs)];
  }, [routes, fromCityId]);

  const destCities = useMemo(() => {
    return routes
      .filter((r) => (!fromCityId || r.from_city_id === fromCityId) && (!toRegion || r.to_region === toRegion))
      .map((r) => ({ id: r.to_city_id, name: r.to_city, region: r.to_region }));
  }, [routes, fromCityId, toRegion]);

  const totals = useMemo(() => {
    if (!activeRoute) return null;
    const fee = Number((isRealtime ? company?.realtime_fee : company?.reservation_fee) || 0);
    const vat = Number(company?.vat_rate || 0);
    const price = Number(activeRoute.price || 0);
    const subtotal = price + fee;
    const tax = subtotal * (vat / 100);
    const total = subtotal + tax;
    return { price, fee, tax, total };
  }, [activeRoute, company, isRealtime]);

  const canGoStep2 = fromCityId && toCityId && (isRealtime || date);
  const canGoStep3 = schedule && pmId && customerFirst && customerLast && customerPhone;

  const submit = async () => {
    setSubmitting(true);
    try {
      const dateStr = (isRealtime ? new Date() : date).toISOString().slice(0, 10);
      const { data } = await api.post("/tickets", {
        kind: isRealtime ? "realtime" : "reservation",
        route_id: activeRoute.id,
        schedule,
        date: dateStr,
        customer_name: customerLast,
        customer_firstname: customerFirst,
        customer_phone: customerPhone,
        payment_method_id: pmId,
        channel: context === "cash" ? "cash" : "menu",
      });
      setTicket(data);
      setStep(4);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Chargement…</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Link to={context === "cash" ? "/app/cash" : "/app"} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-orange-500" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> {context === "cash" ? "Retour à la caisse" : "Retour au menu"}
        </Link>
        <div className="text-xs font-semibold tracking-widest uppercase text-orange-500">
          {context === "cash" ? "Caisse · " : "Menu · "}{isRealtime ? "Billet Temps Réel" : "Billet de Réservation"}
        </div>
      </div>

      <Stepper step={step} />

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <Card>
              <CardHeader><CardTitle className="font-display">Étape 1 — Trajet</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Départ</Label>
                  <Select value={fromCityId} onValueChange={(v) => { setFromCityId(v); setToRegion(""); setToCityId(""); }}>
                    <SelectTrigger data-testid="from-city-select"><SelectValue placeholder="Ville de départ" /></SelectTrigger>
                    <SelectContent>
                      {cities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Région d'arrivée</Label>
                  <Select value={toRegion} onValueChange={(v) => { setToRegion(v); setToCityId(""); }} disabled={!fromCityId}>
                    <SelectTrigger data-testid="to-region-select"><SelectValue placeholder="Région" /></SelectTrigger>
                    <SelectContent>
                      {availableRegions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ville d'arrivée</Label>
                  <Select value={toCityId} onValueChange={setToCityId} disabled={!toRegion}>
                    <SelectTrigger data-testid="to-city-select"><SelectValue placeholder="Ville de destination" /></SelectTrigger>
                    <SelectContent>
                      {destCities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date {isRealtime && <span className="text-xs text-slate-500">(automatique : aujourd'hui)</span>}</Label>
                  {isRealtime ? (
                    <Input value={new Date().toLocaleDateString("fr-FR")} disabled />
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="date-picker-btn">
                          <CalendarIcon className="w-4 h-4 mr-2" /> {date.toLocaleDateString("fr-FR")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                {activeRoute && (
                  <div className="p-3 rounded-xl bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30">
                    <div className="text-xs uppercase tracking-widest text-orange-600 font-semibold">Tarif détecté</div>
                    <div className="font-display font-bold text-xl mt-1">{formatCurrency(activeRoute.price, currency)}</div>
                    <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">{activeRoute.from_city} → {activeRoute.to_city}</div>
                  </div>
                )}
                <div className="flex justify-end pt-2">
                  <Button disabled={!canGoStep2} onClick={() => setStep(2)} data-testid="step1-next-btn"
                    className="btn-pill bg-orange-500 hover:bg-orange-600">Suivant <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <Card>
              <CardHeader><CardTitle className="font-display">Étape 2 — Informations</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Agence</Label>
                    <Input value={company?.name || ""} disabled />
                  </div>
                  <div>
                    <Label>Tarif</Label>
                    <Input value={activeRoute ? formatCurrency(activeRoute.price, currency) : ""} disabled />
                  </div>
                </div>
                <div>
                  <Label>Horaire</Label>
                  <Select value={schedule} onValueChange={setSchedule}>
                    <SelectTrigger data-testid="schedule-select"><SelectValue placeholder="Choisir un horaire" /></SelectTrigger>
                    <SelectContent>
                      {(activeRoute?.schedules || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Moyen de paiement</Label>
                  <Select value={pmId} onValueChange={setPmId}>
                    <SelectTrigger data-testid="payment-method-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>
                      {pms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.test_mode ? " (test)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Prénom</Label>
                    <Input required value={customerFirst} onChange={(e) => setCustomerFirst(e.target.value)} data-testid="customer-firstname-input" />
                  </div>
                  <div>
                    <Label>Nom</Label>
                    <Input required value={customerLast} onChange={(e) => setCustomerLast(e.target.value)} data-testid="customer-lastname-input" />
                  </div>
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input required value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} data-testid="customer-phone-input" placeholder="+223 ..." />
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="btn-pill" data-testid="step2-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
                  <Button disabled={!canGoStep3} onClick={() => setStep(3)} data-testid="step2-next-btn"
                    className="btn-pill bg-orange-500 hover:bg-orange-600">Suivant <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <Card>
              <CardHeader><CardTitle className="font-display">Étape 3 — Validation</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <Info label="Départ" value={activeRoute?.from_city} />
                  <Info label="Destination" value={activeRoute?.to_city} />
                  <Info label="Date" value={(isRealtime ? new Date() : date).toLocaleDateString("fr-FR")} />
                  <Info label="Horaire" value={schedule} />
                  <Info label="Client" value={`${customerFirst} ${customerLast}`} />
                  <Info label="Téléphone" value={customerPhone} />
                  <Info label="Tarif" value={formatCurrency(totals?.price, currency)} />
                  <Info label="Frais de réservation" value={formatCurrency(totals?.fee, currency)} />
                  <Info label="TVA" value={formatCurrency(totals?.tax, currency)} />
                  <Info label="Paiement" value={pms.find(p => p.id === pmId)?.name || ""} />
                </div>
                <Separator />
                <div className="flex items-center justify-between p-4 rounded-2xl bg-orange-500 text-white">
                  <div className="text-sm font-semibold">Montant total</div>
                  <div className="font-display font-bold text-2xl">{formatCurrency(totals?.total, currency)}</div>
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(2)} className="btn-pill" data-testid="step3-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
                  <Button onClick={submit} disabled={submitting} data-testid="validate-ticket-btn"
                    className="btn-pill bg-orange-500 hover:bg-orange-600">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Émission…</> : <><Check className="w-4 h-4 mr-1" /> Valider</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {step === 4 && ticket && (
          <motion.div key="4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <SuccessCard ticket={ticket} currency={currency} isRealtime={isRealtime} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex flex-col p-3 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
      <span className="text-xs text-slate-500 uppercase tracking-wider">{label}</span>
      <span className="font-semibold text-sm mt-1">{value || "—"}</span>
    </div>
  );
}

function Stepper({ step }) {
  const steps = ["Trajet", "Informations", "Validation", "Succès"];
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-2 ${step >= i + 1 ? "text-orange-500" : "text-slate-400"}`}>
            <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${step > i + 1 ? "bg-orange-500 text-white" : step === i + 1 ? "bg-orange-500 text-white" : "bg-slate-200 dark:bg-slate-800"}`}>
              {step > i + 1 ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </div>
            <span className="hidden sm:block text-xs font-semibold">{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-px flex-1 ${step > i + 1 ? "bg-orange-500" : "bg-slate-200 dark:bg-slate-800"}`}></div>}
        </React.Fragment>
      ))}
    </div>
  );
}

function SuccessCard({ ticket, currency, isRealtime }) {
  const qrPayload = `${ticket.ticket_number}|${ticket.qr_secret}`;
  const pdfUrl = `${API_BASE}/tickets/${ticket.id}/pdf`;
  const [rating, setRating] = React.useState(0);
  const [comment, setComment] = React.useState("");
  const [reviewSent, setReviewSent] = React.useState(false);

  const shareTicket = async () => {
    const shareData = { title: `Billet ${ticket.ticket_number}`, text: `${ticket.from_city} → ${ticket.to_city} · ${ticket.date} ${ticket.schedule}`, url: pdfUrl };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {}
    } else {
      navigator.clipboard?.writeText(shareData.text + " " + shareData.url);
      toast.success("Lien copié dans le presse-papier");
    }
  };

  const submitReview = async () => {
    if (!rating) return;
    try {
      await api.post("/reviews", { ticket_id: ticket.id, rating, comment });
      setReviewSent(true);
      toast.success("Merci pour votre avis !");
    } catch (e) {
      toast.error("Erreur lors de l'envoi de l'avis");
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="bg-orange-500 text-white p-6 flex items-center gap-3">
        <PartyPopper className="w-8 h-8" />
        <div>
          <div className="font-display font-bold text-xl">Félicitations !</div>
          <div className="text-sm opacity-90">Vous avez pris votre billet {isRealtime ? "en temps réel. Soyez à l'heure !" : "de réservation."}</div>
        </div>
      </div>
      <CardContent className="p-6 space-y-6">
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <div className="flex justify-center">
            <div className="p-4 bg-white border border-slate-200 rounded-2xl">
              <QRCodeCanvas value={qrPayload} size={180} data-testid="ticket-qrcode" />
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="text-xs uppercase tracking-widest text-orange-500 font-semibold">N° Billet</div>
            <div className="font-display font-bold text-2xl" data-testid="ticket-number">{ticket.ticket_number}</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Info label="Trajet" value={`${ticket.from_city} → ${ticket.to_city}`} />
              <Info label="Date" value={ticket.date} />
              <Info label="Horaire" value={ticket.schedule} />
              <Info label="Client" value={`${ticket.customer_firstname} ${ticket.customer_name}`} />
              <Info label="Paiement" value={ticket.payment_method} />
              <Info label="Total" value={formatCurrency(ticket.total, currency)} />
            </div>
          </div>
        </div>

        <Separator />

        <div className="flex flex-wrap gap-2">
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="download-pdf-btn">
            <Button className="btn-pill bg-orange-500 hover:bg-orange-600"><Download className="w-4 h-4 mr-1" /> Télécharger PDF</Button>
          </a>
          <Button variant="outline" className="btn-pill" onClick={shareTicket} data-testid="share-ticket-btn"><Share2 className="w-4 h-4 mr-1" /> Partager</Button>
          <Link to="/app" data-testid="back-home-btn"><Button variant="outline" className="btn-pill"><Home className="w-4 h-4 mr-1" /> Retour accueil</Button></Link>
        </div>

        <Separator />

        <div>
          <div className="text-sm font-semibold mb-2">Laisser un commentaire</div>
          {reviewSent ? (
            <div className="text-sm text-emerald-600 dark:text-emerald-400">Merci ! Votre avis a été enregistré.</div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-1" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button type="button" key={i} onClick={() => setRating(i)} data-testid={`star-${i}`} className="p-1 transition-transform hover:scale-110">
                    <Star className={`w-6 h-6 ${i <= rating ? "fill-orange-500 stroke-orange-500" : "stroke-slate-400"}`} />
                  </button>
                ))}
              </div>
              <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Votre avis (optionnel)" data-testid="review-comment-input" />
              <Button onClick={submitReview} disabled={!rating} className="btn-pill bg-slate-900 hover:bg-slate-800 text-white" data-testid="submit-review-btn">Envoyer</Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
